import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { notificationService, getStreamUrl, type Notification } from '../../services/notifications'
import { timeAgo } from '@lipaflow/ui'

const categoryStyles: Record<string, string> = {
  info: 'bg-info-100 text-info-700',
  payment: 'bg-success-soft text-success',
  escalation: 'bg-warning-soft text-warning',
  system: 'bg-surface-alt text-ink-muted',
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Poll unread count every 30 seconds
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationService.getUnreadCount,
    refetchInterval: 30000,
  })

  // Fetch recent notifications when panel is open
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => notificationService.list(1, 10),
    enabled: open,
  })

  // SSE connection for real-time notification updates
  useEffect(() => {
    let es: EventSource | null = null
    let retryTimeout: ReturnType<typeof setTimeout>
    // Opening the stream waits for a ticket. Unmounted meanwhile (signing
    // out), the cleanup has already run: a stream opened after it was never
    // closed, and each one held a browser connection to the API until requests
    // queued behind them (seen 2026-10-11 after a few account switches).
    let stopped = false

    const connect = async () => {
      const url = await getStreamUrl()
      if (!url || stopped) return

      es = new EventSource(url)

      es.addEventListener('notification', () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      })

      es.onerror = () => {
        es?.close()
        es = null
        // Retry after 60s — polling continues as fallback
        if (!stopped) retryTimeout = setTimeout(connect, 60_000)
      }
    }

    connect()

    return () => {
      stopped = true
      es?.close()
      clearTimeout(retryTimeout)
    }
  }, [queryClient])

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  // Close panel on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id)
    }
    if (notification.link) {
      navigate(notification.link)
      setOpen(false)
    }
  }

  const unreadCount = unreadData?.count ?? 0
  const notifications = notificationsData?.items ?? []

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-ink-muted hover:text-ink rounded-button hover:bg-surface-alt transition-colors"
        aria-label="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-clay-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-surface rounded-card shadow-xl border border-hairline overflow-hidden z-50">
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
            <h3 className="font-outfit text-h4 text-ink">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                className="inline-flex items-center gap-1 text-caption text-accent hover:text-accent-hover font-medium transition-colors"
              >
                <CheckIcon className="h-3.5 w-3.5" />
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-[400px] overflow-y-auto divide-y divide-hairline">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="w-5 h-5 spinner mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellIcon className="h-8 w-8 text-ink-faint mx-auto" />
                <p className="mt-2 font-inter text-body-sm text-ink-muted">
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={clsx(
                    'w-full px-4 py-3 text-left hover:bg-surface-alt transition-colors',
                    !n.is_read && 'bg-accent-soft/40',
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Unread indicator */}
                    <div className="pt-1.5 flex-shrink-0">
                      <div
                        className={clsx(
                          'w-2 h-2 rounded-full',
                          n.is_read ? 'bg-transparent' : 'bg-accent',
                        )}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p
                          className={clsx(
                            'font-inter text-body-sm truncate',
                            n.is_read ? 'text-ink-muted' : 'text-ink font-medium',
                          )}
                        >
                          {n.title}
                        </p>
                        <span
                          className={clsx(
                            'flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider',
                            categoryStyles[n.category] || categoryStyles.info,
                          )}
                        >
                          {n.category}
                        </span>
                      </div>
                      {n.body && (
                        <p className="font-inter text-caption text-ink-muted mt-0.5 line-clamp-2">
                          {n.body}
                        </p>
                      )}
                      <p className="font-inter text-caption text-ink-faint mt-1">
                        {timeAgo(n.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
